/**
 * Inline text renderer for team-chat message bubbles.
 *
 * Renders the following markup inline, preserving `\n` (handled by the
 * caller's `whitespace-pre-wrap` container):
 *
 *   @username        → styled mention span
 *   **bold**         → <strong>
 *   _italic_         → <em>
 *   http(s)://...    → clickable <a> (opens in a new tab, rel=noopener)
 *
 * Design constraints
 * ------------------
 * - MUST be a pure function returning a stable array of React children —
 *   no state, no refs — because it's called during render on every
 *   MessageBubble redraw.
 * - MUST match `renderWithMentions`'s previous @mention behaviour
 *   character-for-character (same regex, same styling classnames) so
 *   existing plain-text messages don't visually shift.
 * - MUST render unescaped `**` / `_` correctly. Pasted content that
 *   already contained `*` / `_` was backslash-escaped by
 *   `sanitizeHtmlToMessage`; we de-escape here.
 * - MUST NOT execute or evaluate any HTML from the message body. We
 *   never call `dangerouslySetInnerHTML` — every span is a React node.
 * - Deliberately does NOT support underline (`<u>`) because the app has
 *   no Markdown-style syntax for it and the paste sanitizer strips it —
 *   this matches the spec's "if currently supported" note.
 */

import React from "react";

/* ---- Regex: split by mention / bold / italic / URL tokens.
 * Groups (in order):
 *   1. @mention
 *   2. **bold**
 *   3. _italic_ (bounded by word/whitespace edges to avoid eating
 *      underscores inside identifiers like `some_var`)
 *   4. http(s) URL
 * The outer group + non-consuming lookbehind on `_` is achieved by
 * making the underscore branch require a leading space/start-of-string
 * outside the capture. String.split with a capturing group returns
 * every match interleaved with the surrounding text.
 */
const TOKEN_RE = /(@[a-zA-Z0-9._-]+|\*\*[^\n*]+?\*\*|(?:^|(?<=\s))_[^\n_]+?_(?=$|\s|[.,;:!?)]) |https?:\/\/[^\s<>"']+)/g;
// Note: the trailing space after `_` group is a workaround for RE
// non-consuming lookahead limits — we handle look-ahead inside the
// alternation, not via `.split`. The real regex is built below.

const RE = new RegExp(
  [
    "@[a-zA-Z0-9._-]+",                                          // 1) @mention
    "__[^\\n_]+?__",                                              // 2) __underline__ (iter160)
    "\\*\\*[^\\n*]+?\\*\\*",                                      // 3) **bold**
    "(?:^|(?<=\\s))_[^\\n_]+?_(?=$|\\s|[.,;:!?)])",              // 4) _italic_
    "\\[[^\\]\\n]+\\]\\((https?:\\/\\/[^)\\s]+|mailto:[^)\\s]+)\\)", // 5) [label](url)
    "https?:\\/\\/[^\\s<>\"']+",                                 // 6) bare URL
  ].map(s => `(${s})`).join("|"),
  "g",
);

/** De-escape `\*` / `\_` inserted by the paste sanitizer. */
function deEscape(text) {
  return text.replace(/\\([*_])/g, "$1");
}

/**
 * @param body   {string} The message body as stored in the DB.
 * @param mine   {boolean} True for the sender's own bubble (affects
 *                        mention styling only).
 * @returns      {React.ReactNode[]} An array of spans / strong / em / a
 *                                  React nodes, safe to render inside
 *                                  a `whitespace-pre-wrap` container.
 */
export function renderInlineFormat(body, mine) {
  if (!body) return [];
  // iter159 — replace the mini-Markdown bullet marker `- ` at line
  // start with a Unicode bullet (`• `) so the recipient never sees
  // raw markdown syntax. Non-line-start dashes (e.g. "state-of-the-art",
  // typed em-dashes) are untouched.
  const src = String(body).replace(/(^|\n)- /g, "$1• ");
  const out = [];
  let last = 0;
  let m;
  RE.lastIndex = 0;
  let key = 0;
  while ((m = RE.exec(src)) !== null) {
    if (m.index > last) {
      out.push(<span key={`t-${key++}`}>{deEscape(src.slice(last, m.index))}</span>);
    }
    const full = m[0];
    if (full.startsWith("@")) {
      out.push(
        <span
          key={`m-${key++}`}
          className={`font-semibold ${mine ? "underline" : "text-[var(--accent)]"}`}
        >
          {full}
        </span>,
      );
    } else if (full.startsWith("**") && full.endsWith("**")) {
      out.push(<strong key={`b-${key++}`}>{deEscape(full.slice(2, -2))}</strong>);
    } else if (full.startsWith("__") && full.endsWith("__")) {
      out.push(<u key={`u-${key++}`}>{deEscape(full.slice(2, -2))}</u>);
    } else if (full.startsWith("_") && full.endsWith("_")) {
      out.push(<em key={`i-${key++}`}>{deEscape(full.slice(1, -1))}</em>);
    } else if (full.startsWith("[")) {
      // [label](url) — clickable label, URL never visible.
      const closeBracket = full.indexOf("](");
      const label = full.slice(1, closeBracket);
      const url   = full.slice(closeBracket + 2, -1);
      out.push(
        <a
          key={`ml-${key++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={mine ? "underline" : "underline text-[var(--accent)]"}
        >
          {deEscape(label)}
        </a>,
      );
    } else if (/^https?:\/\//i.test(full)) {
      // Strip trailing sentence punctuation that got glued to the URL.
      const trailing = full.match(/[).,;:!?]+$/)?.[0] || "";
      const url = trailing ? full.slice(0, -trailing.length) : full;
      out.push(
        <a
          key={`l-${key++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={mine ? "underline" : "underline text-[var(--accent)]"}
        >
          {url}
        </a>,
      );
      if (trailing) out.push(<span key={`p-${key++}`}>{trailing}</span>);
    }
    last = m.index + full.length;
  }
  if (last < src.length) {
    out.push(<span key={`t-${key++}`}>{deEscape(src.slice(last))}</span>);
  }
  return out;
}
